import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Tag, ChevronLeft, ChevronRight, Star, ArrowRight } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  featured: boolean;
  read_time: string;
  content: string;
  keywords: string[];
  published: boolean;
}

interface BlogSectionProps {
  featuredPosts: BlogPost[];
  currentFeaturedIndex: number;
  onPrevFeatured: () => void;
  onNextFeatured: () => void;
  onSetCurrentFeatured: (index: number) => void;
}

export function BlogSection({
  featuredPosts,
  currentFeaturedIndex,
  onPrevFeatured,
  onNextFeatured,
  onSetCurrentFeatured
}: BlogSectionProps) {
  const navigate = useNavigate();

  // If no featured posts, show placeholder content
  if (featuredPosts.length === 0) {
    return (
      <div className="py-12 bg-bw-app">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-base text-bw-accent font-semibold tracking-wide uppercase">The Winning Bid</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-bw-ink sm:text-4xl">
              Expert Insights & Strategies
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-bw-muted">
              Stay ahead with our latest articles on government contracting, proposal writing, and industry trends.
            </p>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-bw-primary hover:brightness-110 transition"
            >
              Visit The Winning Bid
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentPost = featuredPosts[currentFeaturedIndex];

  return (
    <div className="py-12 bg-bw-app">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-base text-bw-accent font-semibold tracking-wide uppercase">The Winning Bid</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-bw-ink sm:text-4xl">
            Expert Insights & Strategies
          </p>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-bw-muted">
            Stay ahead with our latest articles on government contracting, proposal writing, and industry trends.
          </p>
        </div>

        <div className="mb-12 relative">
          <div className="relative bg-bw-sidebar rounded-xl shadow-panel overflow-hidden">
            <div className="relative p-12">
              <div className="relative text-center text-white">
                <div className="flex items-center justify-center text-sm text-white/80 mb-4">
                  <Star className="h-5 w-5 mr-2" />
                  <span className="font-semibold">Featured Article</span>
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  {currentPost.title}
                </h2>
                <p className="text-white/90 mb-6 line-clamp-3 max-w-3xl mx-auto">
                  {currentPost.excerpt}
                </p>
                <div className="flex items-center justify-center space-x-8">
                  <div className="flex items-center text-white/80">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>{currentPost.date}</span>
                  </div>
                  <div className="flex items-center text-white/80">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>{currentPost.read_time}</span>
                  </div>
                  <div className="flex items-center text-white/80">
                    <Tag className="h-4 w-4 mr-2" />
                    <span>{currentPost.category}</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/blog/${currentPost.id}`)}
                  className="mt-8 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-bw-sidebar bg-white hover:bg-white/90 transition-colors"
                >
                  Read Article
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {featuredPosts.length > 1 && (
            <>
              <button
                onClick={onPrevFeatured}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={onNextFeatured}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                {featuredPosts.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => onSetCurrentFeatured(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentFeaturedIndex
                        ? 'bg-white'
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-bw-primary hover:brightness-110 transition"
          >
            Visit The Winning Bid
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
